import { Fragment, useState, type ReactNode, type ButtonHTMLAttributes } from 'react';
import { Link } from 'react-router-dom';
import { formatMoney, moneySign } from '../lib/format';

// Shared UI primitives for the dense finance aesthetic. Pages compose these; the visual
// language lives here + index.css so every screen reads as one system.

type Tone = 'pos' | 'neg' | 'warn' | 'info' | 'muted';

// ── Money ───────────────────────────────────────────────────────────────────
export function Money({
  minor,
  currency = 'USD',
  color = false,
  className = '',
}: {
  minor: string | number | null | undefined;
  currency?: string;
  color?: boolean; // color by sign (credit/debit)
  className?: string;
}) {
  const sign = color ? moneySign(minor) : '';
  return <span className={`money ${sign} tabular ${className}`}>{formatMoney(minor, currency)}</span>;
}

// ── Badge / status ───────────────────────────────────────────────────────────
export function Badge({ tone = 'muted', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span className={`badge ${tone}`}>
      <span className="tick" />
      {children}
    </span>
  );
}

// Maps common domain statuses to a tone + label. Covers invoice, contract, timesheet,
// recon, and generic active/inactive states.
const STATUS_TONE: Record<string, Tone> = {
  // invoice
  draft: 'muted', issued: 'info', frozen: 'warn', voided: 'neg', paid: 'pos', overdue: 'neg',
  // contract / generic
  pending: 'warn', active: 'pos', expired: 'muted', cancelled: 'neg', inactive: 'muted',
  // timesheet
  open: 'info', locked: 'pos',
  // recon
  unresolved: 'neg', resolved: 'pos', investigating: 'warn',
  // credit note kind
  credit_note: 'info', invoice: 'muted',
};

export function StatusBadge({ status }: { status: string | null | undefined }) {
  const s = (status ?? 'unknown').toLowerCase();
  const tone = STATUS_TONE[s] ?? 'muted';
  return <Badge tone={tone}>{(status ?? '—').replace(/_/g, ' ')}</Badge>;
}

// ── Lifecycle rail ───────────────────────────────────────────────────────────
// A compact horizontal record-lifecycle indicator (e.g. invoice draft → issued →
// frozen). 'terminal' marks a dead end (voided). Bars fill up to the reached step.
export interface LifecycleStep {
  label: string;
  state: 'done' | 'current' | 'upcoming' | 'terminal';
}

export function LifecycleRail({ steps }: { steps: LifecycleStep[] }) {
  return (
    <div className="lifecycle" role="list" aria-label="Lifecycle">
      {steps.map((s, i) => (
        <Fragment key={i}>
          {i > 0 && <span className={`lc-bar ${s.state !== 'upcoming' ? 'filled' : ''}`} aria-hidden />}
          <span role="listitem" className={`lc-step ${s.state}`} aria-current={s.state === 'current' || undefined}>
            <span className="lc-dot" aria-hidden />
            {s.label}
          </span>
        </Fragment>
      ))}
    </div>
  );
}

// ── Meter ────────────────────────────────────────────────────────────────────
// Thin progress bar (prepay burn-down, invoice collection). fraction is clamped
// to [0,1]; tone shifts the fill toward warning/exhausted.
export function Meter({
  fraction,
  tone,
  title,
}: {
  fraction: number;
  tone?: 'warn' | 'neg';
  title?: string;
}) {
  const w = Number.isFinite(fraction) ? Math.max(0, Math.min(1, fraction)) : 0;
  return (
    <span
      className={`meter ${tone ?? ''}`}
      title={title}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(w * 100)}
    >
      <i style={{ ['--w' as string]: String(w) }} />
    </span>
  );
}

// ── Inline editing ───────────────────────────────────────────────────────────
// Click-to-edit text: the value looks like text, edits like a field. Enter/blur saves
// (only when changed), Esc cancels. A typo should never require an API console.
export function EditableText({
  value,
  onSave,
  strong,
  'aria-label': ariaLabel,
}: {
  value: string;
  onSave: (next: string) => void | Promise<void>;
  strong?: boolean;
  'aria-label'?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (!editing) {
    return (
      <button
        type="button"
        className="editable-text"
        title="Click to edit"
        aria-label={ariaLabel ? `Edit ${ariaLabel}` : 'Edit'}
        onClick={(e) => {
          e.stopPropagation();
          setDraft(value);
          setEditing(true);
        }}
      >
        {strong ? <strong>{value}</strong> : value}
        <span className="et-pen" aria-hidden>
          ✎
        </span>
      </button>
    );
  }

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== value) void onSave(next);
  };

  return (
    <input
      className="editable-input"
      value={draft}
      autoFocus
      aria-label={ariaLabel}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') setEditing(false);
      }}
    />
  );
}

// ── Buttons ──────────────────────────────────────────────────────────────────
export function Button({
  variant = 'default',
  size,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'default' | 'primary' | 'danger' | 'ghost';
  size?: 'sm';
}) {
  const cls = ['btn', variant !== 'default' ? variant : '', size ?? ''].filter(Boolean).join(' ');
  return (
    <button className={cls} {...rest}>
      {children}
    </button>
  );
}

// ── Cards / stats ────────────────────────────────────────────────────────────
export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`card ${className}`}>{children}</div>;
}

export function CardHeader({ title, action }: { title: ReactNode; action?: ReactNode }) {
  return (
    <div className="card-header">
      <h3 className="card-title">{title}</h3>
      {action}
    </div>
  );
}

export function Stat({
  label,
  value,
  sub,
  accent = false,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  accent?: boolean;
}) {
  return (
    <div className={`stat ${accent ? 'accent' : ''}`}>
      <div className="label">{label}</div>
      <div className="value tabular">{value}</div>
      {sub != null && <div className="sub">{sub}</div>}
    </div>
  );
}

// ── Page header + breadcrumb ─────────────────────────────────────────────────
export function PageHeader({
  title,
  subtitle,
  actions,
  breadcrumb,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  breadcrumb?: { label: string; to?: string }[];
}) {
  return (
    <div>
      {breadcrumb && (
        <div className="breadcrumb">
          {breadcrumb.map((b, i) => (
            <span key={i}>
              {b.to ? <Link to={b.to}>{b.label}</Link> : b.label}
              {i < breadcrumb.length - 1 && ' / '}
            </span>
          ))}
        </div>
      )}
      <div className="page-header">
        <div className="titles">
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {actions && <div className="actions">{actions}</div>}
      </div>
    </div>
  );
}

// ── Generic data table ───────────────────────────────────────────────────────
export interface Column<T> {
  header: ReactNode;
  cell: (row: T) => ReactNode;
  align?: 'right';
  width?: string;
}

export function DataTable<T>({
  columns,
  rows,
  keyOf,
  onRowClick,
  empty,
  footer,
}: {
  columns: Column<T>[];
  rows: T[];
  keyOf: (row: T, i: number) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  /** Rendered inside the card below the table (load-more, truncation notices). */
  footer?: ReactNode;
}) {
  if (rows.length === 0) return <>{empty ?? <EmptyState title="Nothing here yet" />}</>;

  // Keyboard-first rows: clickable rows are focusable; Enter/Space opens, arrows move.
  const onKey = (e: React.KeyboardEvent<HTMLTableRowElement>, row: T) => {
    if (!onRowClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick(row);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const tr = e.currentTarget;
      const sib = e.key === 'ArrowDown' ? tr.nextElementSibling : tr.previousElementSibling;
      (sib as HTMLElement | null)?.focus();
    }
  };

  return (
    <div className="table-card">
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              {columns.map((c, i) => (
                <th key={i} className={c.align === 'right' ? 'num' : ''} style={c.width ? { width: c.width } : undefined}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={keyOf(row, i)}
                className={onRowClick ? 'clickable' : ''}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={onRowClick ? (e) => onKey(e, row) : undefined}
              >
                {columns.map((c, j) => (
                  <td key={j} className={c.align === 'right' ? 'num' : ''}>
                    {c.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer}
    </div>
  );
}

/** Honest pagination: shows what's loaded and fetches the next page on demand. */
export function LoadMore({
  loaded,
  hasMore,
  loading,
  onMore,
}: {
  loaded: number;
  hasMore: boolean;
  loading?: boolean;
  onMore: () => void;
}) {
  if (!hasMore) return null;
  return (
    <div className="load-more">
      <span>{loaded} loaded</span>
      <Button size="sm" onClick={onMore} disabled={loading}>
        {loading ? 'Loading…' : 'Load more'}
      </Button>
    </div>
  );
}

// ── States ───────────────────────────────────────────────────────────────────
export function EmptyState({ title, hint, icon = '∅', action }: { title: string; hint?: string; icon?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="icon">{icon}</div>
      <h3>{title}</h3>
      {hint && <p className="muted">{hint}</p>}
      {action && <div style={{ marginTop: 'var(--sp-3)' }}>{action}</div>}
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="loading-row">
      <span className="spinner" /> {label}
    </div>
  );
}

export function ErrorBanner({ error }: { error: unknown }) {
  const msg = error instanceof Error ? error.message : String(error);
  return <div className="error-banner">⚠ {msg}</div>;
}

// A tiny async-state helper: renders loading / error / children.
export function Async<T>({
  query,
  children,
}: {
  query: { data?: T; isLoading: boolean; error: unknown };
  children: (data: T) => ReactNode;
}) {
  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorBanner error={query.error} />;
  if (query.data === undefined) return null;
  return <>{children(query.data)}</>;
}
